/**
 * Text of the new schedule page: the prototype's sentence builders, with every word in `sch6.*` and every number through Intl.
 * `makeFmt(t, lang)` returns the helpers bound to a translate function. Pure (no React): the tests drive it with i18next.
 */
import { dayDiff, dowOf, hmsToMin, localParts, maskToDays, ymdParts } from './model';

const sameDays = (a, b) => a.length === b.length && a.every((x, i) => x === b[i]);

export function makeFmt(t, lang = 'ar') {
  const loc = lang === 'ar' ? 'ar-EG' : 'en-US';
  const nf = new Intl.NumberFormat(loc, { useGrouping: false });
  /** Intl digits of a non-negative integer (ar-EG gives the Arabic-Indic digits the platform uses). */
  const num = (n) => nf.format(n);
  const two = (n) => num(Math.floor(n / 10)) + num(n % 10);

  const dayName = (i) => t(`sch6.day_${i}`);
  const dayShort = (i) => t(`sch6.dayS_${i}`);
  const dayLetter = (i) => t(`sch6.dayL_${i}`);
  const monthName = (i) => t(`sch6.mon_${i}`);

  /** '١٠:١٥ ص' — minutes since midnight, 12-hour clock. */
  const tm = (min) => {
    const m = ((min % 1440) + 1440) % 1440;
    const h = Math.floor(m / 60);
    const mm = m % 60;
    return `${num(h % 12 === 0 ? 12 : h % 12)}:${two(mm)} ${t(h < 12 ? 'sch6.am' : 'sch6.pm')}`;
  };

  /** The same with ':٠٠' dropped for whole hours (the preset chips: «٦ ص – ١٢ م»). */
  const tmShort = (min) => tm(min).replace(`:${two(0)}`, '');

  /** '23 سبتمبر' / 'الخميس ٢٢ أكتوبر' / with a year when it is not `thisYear`. */
  const dtxt = (ymd, withDay, thisYear) => {
    const { y, m, d } = ymdParts(ymd);
    return `${withDay ? `${dayName(dowOf(ymd))} ` : ''}${num(d)} ${monthName(m - 1)}${y !== thisYear ? ` ${num(y)}` : ''}`;
  };

  /** '٣ شاشات' with the Arabic number agreement of the prototype (1, 2, 3-10, 11+). */
  const nScreens = (n) => t(n === 1 ? 'sch6.screens_one' : n === 2 ? 'sch6.screens_two' : n >= 3 && n <= 10 ? 'sch6.screens_few' : 'sch6.screens_many', { n: num(n) });

  const listNames = (names, max) => (names.length <= max
    ? names.join(t('sch6.list_sep'))
    : t('sch6.names_more', { names: names.slice(0, max).join(t('sch6.list_sep')), count: num(names.length - max) }));

  const daysTxt = (days) => {
    const s = [...days].sort((a, b) => a - b);
    if (sameDays(s, [0, 1, 2, 3, 4, 5, 6])) return t('sch6.d_every');
    if (sameDays(s, [0, 1, 2, 3, 4])) return t('sch6.d_workweek');
    if (sameDays(s, [5, 6])) return t('sch6.d_weekend');
    if (s.length === 1) return t('sch6.d_one', { day: dayName(s[0]) });
    return s.map(dayName).join(t('sch6.list_sep'));
  };

  /** from === to is "all day"; a window that ends at or before it starts (but not at midnight) runs into the next day. */
  const rangeTxt = (a, b) => {
    if (a === b) return t('sch6.r_allday');
    if (b === 1440) return t('sch6.range_to_eod', { a: tm(a) });
    const base = t('sch6.r_from_to', { a: tm(a), b: tm(b) });
    return b <= a && b !== 0 ? t('sch6.r_next_day', { base }) : base;
  };

  /** "Duration: …" line of the event step. */
  const durationTxt = (start, end, thisYear) => {
    const n = dayDiff(start, end) + 1;
    const a = dtxt(start, true, thisYear);
    const b = dtxt(end, true, thisYear);
    if (n === 1) return t('sch6.dur_one', { a, b });
    return t(n > 10 ? 'sch6.dur_many' : 'sch6.dur_few', { n: num(n), a, b });
  };

  const daysCountTxt = (n) => t(n > 10 ? 'sch6.days_many' : 'sch6.days_few', { n: num(n) });
  const startsInTxt = (n) => (n === 1 ? t('sch6.starts_tomorrow') : t(n > 10 ? 'sch6.starts_in_many' : 'sch6.starts_in_few', { n: num(n) }));

  /**
   * Where a rule applies. `ctx` = { screens: [{id,name}], groups: [{id,name}], total }. An unknown id is skipped, a
   * legacy mixed rule falls back to its screen count.
   */
  const whereTxt = (w, ctx, screensCount) => {
    if (w.mode === 'all') return t('sch6.where_all', { total: num(ctx.total) });
    if (w.mode === 'groups') return listNames(w.ids.map((id) => ctx.groups.find((g) => g.id === id)?.name).filter(Boolean), 2);
    const names = w.ids.map((id) => ctx.screens.find((s) => s.id === id)?.name).filter(Boolean);
    if (!names.length && screensCount != null) return nScreens(screensCount);
    return t('sch6.where_screens', { count: nScreens(w.ids.length), names: listNames(names, 2) });
  };

  /* ── conflicts (API `POST /campaign-schedules/conflicts` -> conflicts[]) ── */

  /** Who clashes: every screen, up to two names, or a count. `c.sample_screens` carries up to three names. */
  const sharedTxt = (c, total) => {
    if (c.shared_screens >= total && total > 0) return t('sch6.shared_all');
    if (c.shared_screens <= 2) {
      const n = (c.sample_screens || []).slice(0, c.shared_screens);
      return n.length === 2 ? t('sch6.shared_two', { a: n[0], b: n[1] }) : (n[0] ?? nScreens(c.shared_screens));
    }
    return nScreens(c.shared_screens);
  };

  /**
   * One conflict -> the box's sentence. For a weekly rule: the days both run on and the first overlapping time range; for an
   * event: the first overlapping date.
   */
  const conflictTxt = (c, form, total, thisYear, today) => {
    const shared = sharedTxt(c, total);
    if (form.kind === 'once') {
      const when = form.odate === today ? t('sch6.today') : dtxt(form.odate, true, thisYear);
      return t('sch6.c_once', { name: c.name || c.campaign_name || '', when, shared });
    }
    if (form.kind === 'event') {
      const first = c.overlap?.first_date ?? form.start;
      return t('sch6.c_event', { name: c.name || c.campaign_name || '', date: dtxt(first, true, thisYear), shared });
    }
    const theirs = maskToDays(c.days_of_week);
    const both = form.days.filter((d) => theirs.includes(d));
    const days = daysTxt(both.length ? both : form.days);
    const a = c.overlap ? hmsToMin(c.overlap.start_time) : null;
    const b = c.overlap ? hmsToMin(c.overlap.end_time) : null;
    return t('sch6.c_rule', {
      name: c.campaign_name || c.name || '',
      days,
      from: a === null ? '' : tm(a),
      to: b === null ? '' : tm(b),
      shared,
    });
  };

  /** The name a one-time show gets when the person leaves the field empty: «show — today 10:15 AM». */
  const onceAutoName = (showName, date, fromMin, today, thisYear) => `${showName} — ${date === today ? t('sch6.today') : dtxt(date, true, thisYear)} ${tm(fromMin)}`;

  /** "Until" of a hold / preview: the time, plus the day when it is not today. `tz` = the screen's zone. */
  const untilTxt = (untilLocal, untilAtIso, tz, today, thisYear) => {
    const p = localParts(Date.parse(untilAtIso), tz);
    return `${tm(hmsToMin(untilLocal))}${p.date !== today ? ` ${dtxt(p.date, true, thisYear)}` : ''}`;
  };

  /**
   * «يدوي بدل جدول «name» حتى 5:12 م» for a `schedule_paused` block: the one wording of the screens page, the device page and the
   * schedule page, and one end time (the block's `until_local`, with the day when it is not today in the screen's zone).
   */
  const heldChipTxt = (paused, tz, nowMs = Date.now()) => {
    let time;
    try {
      const p = localParts(nowMs, tz);
      time = untilTxt(paused.until_local, paused.until_at, tz, p.date, p.y);
    } catch (e) {
      time = tm(hmsToMin(paused.until_local));
    }
    return paused.schedule_name
      ? t('screen.schedule_paused_by', { name: paused.schedule_name, time })
      : t('screen.schedule_paused_by_noname', { time });
  };

  return { onceAutoName, untilTxt, heldChipTxt, num, two, tm, tmShort, dtxt, nScreens, listNames, daysTxt, rangeTxt, durationTxt, daysCountTxt, startsInTxt, whereTxt, sharedTxt, conflictTxt, dayName, dayShort, dayLetter, monthName };
}
